import {inject, Injectable} from '@angular/core';
import {GetRecordsTableDataParams, GetRecordsTotalDataParams} from "../types/record-table-container.types";
import {Pageable} from "../../../types/pagable.types";
import {RecordData, RecordTotalData} from "../../../types/records-table.types";
import {defaultPageableRecordData} from "../../../models/default-pageable-record-data";
import {RecordsTableService} from "../../../services/records-table.service";
import {RecordCalendarIdsStateService} from "../../../services/record-calendar-ids-state.service";
import {RecordTableApiService} from "../../../services-api/record-table-api.service";
import {TelegramNotificationsPollingService} from "./telegram-notifications-polling.service";
import {TelegramNotificationsWithPollingParams} from "../types/telegram-notifications-polling.types";
import {TelegramNotificationsStateService} from "../../../services/telegram-notifications-state.service";
import {TelegramNotifications} from "../../../types/telegram-notifications.types";
import {telegramNotificationsPollingInterval} from "../../../models/polling-config";
import {RecordTableAction} from "../../../types/record-table-action.types";
import {ChatHistoryParams} from "../../../types/chat-history.types";
import {TelegramChatHistoryStateService} from "../../../services/telegram-chat-history-state.service";
import {pathsToFetchNotifications} from "../configs/record-table-data-configs";

@Injectable({
  providedIn: 'root'
})
export class RecordTableDataService {
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private recordCalendarIdsStateService: RecordCalendarIdsStateService = inject(RecordCalendarIdsStateService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private telegramNotificationsPollingService: TelegramNotificationsPollingService = inject(TelegramNotificationsPollingService);
  private telegramNotificationsStateService: TelegramNotificationsStateService = inject(TelegramNotificationsStateService);
  private telegramChatHistoryStateService: TelegramChatHistoryStateService = inject(TelegramChatHistoryStateService);

  public async getRecordsTableData(getRecordsTableDataParams: GetRecordsTableDataParams): Promise<Pageable<RecordData>> {
    const { path, queryParams } = getRecordsTableDataParams;

    return await this.recordsTableService.getRecordData(path, queryParams)
      .then((data) => {
        /*TODO: раскомментировать если включат telegram
        this.getTelegramNotifications(data.content, path);*/
        return data;
      })
      .catch(() => defaultPageableRecordData);
  }

  public async getRecordsTotalData(getRecordsTotalDataParams: GetRecordsTotalDataParams): Promise<Array<RecordTotalData>> {
    const { path, queryParams, recordsTableActions } = getRecordsTotalDataParams;

    return this.isNeedRecordTableTotalsByMeta(recordsTableActions) ?
      await this.recordsTableService.getRecordTableTotals(path, queryParams).catch(() => []) :
      [];
  }

  private isNeedRecordTableTotalsByMeta(recordsTableActions: Array<RecordTableAction>): boolean {
    return recordsTableActions.includes('COLUMN_AGGREGATION');
  }

  private async getTelegramNotifications(recordsTableData: Array<RecordData>, path: string): Promise<void> {
    if (!pathsToFetchNotifications.includes(path)) {
      this.telegramNotificationsStateService.resetTelegramNotificationsState();
      this.telegramNotificationsPollingService.stopPolling();
      this.telegramChatHistoryStateService.resetTelegramChatHistoryState();
      return;
    }

    this.recordCalendarIdsStateService.setRecordCalendarIdsState(recordsTableData);
    this.telegramChatHistoryStateService.setTelegramChatHistoryState(await this.getTelegramChatHistory());
    this.getTelegramNotificationsWithPolling({
      intervalMs: telegramNotificationsPollingInterval,
      callback: (telegramNotifications) => {
        if (this.hasTelegramNotificationsChanged(telegramNotifications)) {
          this.telegramNotificationsStateService.setTelegramNotificationsState(telegramNotifications);
        }
      }
    });
  }

  private getTelegramNotificationsWithPolling({ intervalMs, callback }: TelegramNotificationsWithPollingParams): void {
    const requestFn = () => this.recordTableApiService.getTelegramNotifications(this.recordCalendarIdsStateService.recordCalendarIdsState());
    this.telegramNotificationsPollingService.startPolling({ intervalMs, requestFn, callback });
  }

  private hasTelegramNotificationsChanged(newTelegramNotifications: Array<TelegramNotifications>): boolean {
    const oldNotifications = this.telegramNotificationsStateService.telegramNotificationsState();
    if (oldNotifications.length !== newTelegramNotifications.length) { return true; }

    const oldMap = new Map(oldNotifications.map(n => [n.recordCalendarId, n.notificationCount]));
    for (const newNotif of newTelegramNotifications) {
      if (oldMap.get(newNotif.recordCalendarId) !== newNotif.notificationCount) {
        return true;
      }
    }

    return false;
  }

  private async getTelegramChatHistory(): Promise<Array<ChatHistoryParams>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.getTelegramChatHistory(this.recordCalendarIdsStateService.recordCalendarIdsState()).subscribe({
        next: (chatHistoryParams: Array<ChatHistoryParams>) => resolve(chatHistoryParams),
        error: (error) => reject(error)
      });
    });
  }
}
